// ── Employee Performance widget: the drill-down's data ─────────────────
//
// GET /api/dashboard/employee-performance/transactions takes the widget's own parameters plus an
// employeeId, and answers with that employee's receipts for the same view — newest first, capped —
// and totals over every one of them, so the totals are the employee's row in the widget. Nothing
// about the customer comes back. Each receipt's items come from a sibling endpoint, the same view
// plus the receipt (the last section below).

import {
    EmployeePerformanceError,
    employeePerformanceParams,
    fetchEmployeeJson,
    type EmployeeColumnContext,
    type EmployeePerformanceFilters,
    type EmployeePerformancePeriod,
    type SortDir,
} from './employeePerformance'

export interface EmployeeTransaction {
    transactionId: string
    /** The till's own clock: yyyy-MM-ddTHH:mm:ss, with no offset. */
    time: string
    storeId: string | null
    storeName: string | null
    till: number | null
    receiptNumber: string | null
    channel: 'instore' | 'online'
    units: number
    /** After line discounts — Gross Sales when returns are excluded, as on the widget. */
    netSales: number
    discounts: number
    /** Adventure Bucks credited on this receipt. */
    bucksCredited: number
    /** Some line on the receipt was a return (even one GROSS leaves out of the figures). */
    hasReturn: boolean
}

export interface EmployeeTransactionTotals {
    transactions: number
    units: number
    netSales: number
    discounts: number
    bucksCredited: number
}

export interface EmployeeTransactionsData {
    employeeId: string
    /** Inclusive yyyy-MM-dd bounds of the window the server queried. */
    startDate: string
    endDate: string
    /** The newest receipts, at most `limit`. */
    transactions: EmployeeTransaction[]
    /** Every matching receipt, including any the list stopped short of. */
    totals: EmployeeTransactionTotals
    /** More receipts matched than are listed. */
    truncated: boolean
    limit: number
}

/** The widget's own query string, plus the employee — so the drill-down covers exactly its view. */
export function buildEmployeeTransactionsUrl(
    period: EmployeePerformancePeriod, filters: EmployeePerformanceFilters, employeeId: string,
): string {
    const p = employeePerformanceParams(period, filters)
    p.set('employeeId', employeeId)
    return `/api/dashboard/employee-performance/transactions?${p.toString()}`
}

export function fetchEmployeeTransactions(url: string, token: string | null): Promise<EmployeeTransactionsData> {
    return fetchEmployeeJson<EmployeeTransactionsData>(url, token)
}

/** The user-facing sentence for a failed load; never echoes server internals. */
export function describeEmployeeTransactionsError(err: unknown): string {
    if (err instanceof EmployeePerformanceError) {
        if (err.status === 403) return err.message || "You don't have access to these transactions."
        if (err.status === 400) return err.message || 'These filters can’t be shown as transactions.'
    }
    return 'The transactions could not be loaded. Close this and try again.'
}

const timeFmt = new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' })
const dayFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' })
const dayYearFmt = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/**
 * A receipt's time as the drill-down lists it: the time alone when the view is one day, with the
 * date when it is longer, and the year too when it spans years. Built from the parts rather than
 * parsed, so the browser's time zone never shifts the till's clock.
 */
export function formatTransactionTime(time: string, startDate: string, endDate: string): string {
    const [day, clock = '00:00:00'] = time.split('T')
    const [y, mo, d] = day.split('-').map(Number)
    const [h, mi] = clock.split(':').map(Number)
    const at = new Date(y, mo - 1, d, h, mi)
    if (Number.isNaN(at.getTime())) return time
    // Some ICU versions put a narrow no-break space before AM/PM; every browser shows the same text.
    const clockText = timeFmt.format(at).replace(/ /g, ' ')
    if (startDate === endDate) return clockText
    const date = (startDate.slice(0, 4) === endDate.slice(0, 4) ? dayFmt : dayYearFmt).format(at)
    return `${date}, ${clockText}`
}

// ── Columns: their order, and the sort ──────────────────────────────
//
// Any column's heading sorts the receipts, and the columns can be put in any order. A sort orders
// the receipts listed and nothing else — past the server's cap the rest aren't here to order, and
// the dialog says so — and lasts while the dialog is open, like the widget's own header sort. The
// order is the viewer's own arrangement, remembered by this browser: storage can be missing or
// refuse, and the canonical order is always a working table.

export type TransactionColumnKey =
    | 'time' | 'store' | 'receipt' | 'channel' | 'units' | 'sales' | 'discounts' | 'bucks' | 'return'

/** The totals figures a column can stand over. */
type TransactionTotalKey = Exclude<keyof EmployeeTransactionTotals, 'transactions'>

export interface TransactionColumnDef {
    key: TransactionColumnKey
    /** Header text. The sales column is named the widget's way: Gross Sales under GROSS. */
    label: (ctx: EmployeeColumnContext) => string
    /** Header tooltip, where the heading needs explaining. */
    description?: (ctx: EmployeeColumnContext) => string
    /** Right-aligned figures. */
    numeric: boolean
    /** The direction a first click sorts in. */
    defaultDir: SortDir
    /** Each direction in words, for the announcement a sort makes. */
    dirWords: Readonly<Record<SortDir, string>>
    /** The figure the totals row shows under this column. The others carry the Total label, or nothing. */
    total?: TransactionTotalKey
}

export interface TransactionSort {
    key: TransactionColumnKey
    dir: SortDir
}

const BY_TIME = { asc: 'oldest first', desc: 'newest first' } as const
const BY_NAME = { asc: 'A to Z', desc: 'Z to A' } as const
const BY_AMOUNT = { asc: 'lowest first', desc: 'highest first' } as const

/** Canonical order — also the default. */
export const TRANSACTION_COLUMNS: readonly TransactionColumnDef[] = [
    { key: 'time', label: () => 'Time', numeric: false, defaultDir: 'desc', dirWords: BY_TIME },
    { key: 'store', label: () => 'Store', numeric: false, defaultDir: 'asc', dirWords: BY_NAME },
    { key: 'receipt', label: () => 'Receipt', numeric: false, defaultDir: 'asc', dirWords: BY_AMOUNT },
    { key: 'channel', label: () => 'Channel', numeric: false, defaultDir: 'asc', dirWords: BY_NAME },
    { key: 'units', label: () => 'Units', numeric: true, defaultDir: 'desc', dirWords: BY_AMOUNT, total: 'units' },
    {
        key: 'sales', label: ctx => (ctx.includeReturns ? 'Net Sales' : 'Gross Sales'),
        numeric: true, defaultDir: 'desc', dirWords: BY_AMOUNT, total: 'netSales',
    },
    { key: 'discounts', label: () => 'Discounts', numeric: true, defaultDir: 'desc', dirWords: BY_AMOUNT, total: 'discounts' },
    {
        key: 'bucks', label: () => 'Adv. Bucks', description: () => 'Adventure Bucks credited to the customer on this receipt.',
        numeric: true, defaultDir: 'desc', dirWords: BY_AMOUNT, total: 'bucksCredited',
    },
    {
        key: 'return', label: () => 'Return',
        description: ctx => (ctx.includeReturns
            ? 'The receipt includes a returned item.'
            : 'The receipt includes a returned item. With GROSS selected, the return isn’t in these figures.'),
        // A first click brings the receipts with a return on them to the top.
        numeric: false, defaultDir: 'desc', dirWords: { asc: 'returns last', desc: 'returns first' },
    },
]

const TRANSACTION_COLUMN_BY_KEY = new Map(TRANSACTION_COLUMNS.map(c => [c.key, c]))

export function getTransactionColumn(key: TransactionColumnKey): TransactionColumnDef {
    return TRANSACTION_COLUMN_BY_KEY.get(key)!
}

export function isTransactionColumnKey(key: unknown): key is TransactionColumnKey {
    return typeof key === 'string' && TRANSACTION_COLUMN_BY_KEY.has(key as TransactionColumnKey)
}

/** The server's own order: newest first. */
export const DEFAULT_TRANSACTION_SORT: TransactionSort = { key: 'time', dir: 'desc' }

export function isDefaultTransactionSort(sort: TransactionSort): boolean {
    return sort.key === DEFAULT_TRANSACTION_SORT.key && sort.dir === DEFAULT_TRANSACTION_SORT.dir
}

/** A header click: the sorted column flips, any other starts at its own default direction. */
export function nextTransactionSort(current: TransactionSort, key: TransactionColumnKey): TransactionSort {
    if (current.key === key) return { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
    return { key, dir: getTransactionColumn(key).defaultDir }
}

export function defaultTransactionColumnOrder(): TransactionColumnKey[] {
    return TRANSACTION_COLUMNS.map(c => c.key)
}

export function isDefaultTransactionColumnOrder(order: readonly TransactionColumnKey[]): boolean {
    return order.length === TRANSACTION_COLUMNS.length && order.every((key, i) => key === TRANSACTION_COLUMNS[i].key)
}

/**
 * Reconciles a remembered order with the canonical one: unknown and repeated keys are dropped, and
 * any column it has never heard of joins at the end — so a column added later still shows up for
 * someone who arranged theirs before it existed. Always returns a fresh array.
 */
export function normalizeTransactionColumnOrder(saved: unknown): TransactionColumnKey[] {
    if (!Array.isArray(saved)) return defaultTransactionColumnOrder()
    const out: TransactionColumnKey[] = []
    for (const key of saved) {
        if (isTransactionColumnKey(key) && !out.includes(key)) out.push(key)
    }
    for (const def of TRANSACTION_COLUMNS) {
        if (!out.includes(def.key)) out.push(def.key)
    }
    return out
}

/** Moves the column at `from` to index `to` (clamped); returns a new array. */
export function moveTransactionColumn(order: readonly TransactionColumnKey[], from: number, to: number): TransactionColumnKey[] {
    if (from < 0 || from >= order.length) return order.slice()
    const target = Math.max(0, Math.min(order.length - 1, to))
    const next = order.slice()
    const [key] = next.splice(from, 1)
    next.splice(target, 0, key)
    return next
}

/** Where this browser keeps the viewer's column order. */
export const TRANSACTION_COLUMN_ORDER_STORAGE_KEY = 'employee-transactions-column-order'

/** The order this browser remembers, or the canonical one. */
export function loadTransactionColumnOrder(): TransactionColumnKey[] {
    try {
        const raw = window.localStorage.getItem(TRANSACTION_COLUMN_ORDER_STORAGE_KEY)
        return normalizeTransactionColumnOrder(raw === null ? null : JSON.parse(raw))
    } catch {
        // Blocked, or not JSON: the canonical order is a perfectly good table.
        return defaultTransactionColumnOrder()
    }
}

/**
 * Remembers an order. The canonical one is remembered by forgetting, so whoever resets gets any
 * later change to the default along with everyone else.
 */
export function saveTransactionColumnOrder(order: readonly TransactionColumnKey[]): void {
    try {
        if (isDefaultTransactionColumnOrder(order)) window.localStorage.removeItem(TRANSACTION_COLUMN_ORDER_STORAGE_KEY)
        else window.localStorage.setItem(TRANSACTION_COLUMN_ORDER_STORAGE_KEY, JSON.stringify(order))
    } catch {
        // Full, or blocked. The order still holds while the dialog is open.
    }
}

const CHANNEL_NAME: Record<string, string> = { instore: 'In-store', online: 'Online' }

/** A receipt's channel as the drill-down shows it — and sorts it. */
export function transactionChannelName(channel: string): string {
    return CHANNEL_NAME[channel] ?? channel
}

// Receipt numbers compare as numbers (#999 before #1000); names compare without regard to case.
const textCollator = new Intl.Collator('en-CA', { sensitivity: 'base', numeric: true })

function transactionSortValue(key: TransactionColumnKey, t: EmployeeTransaction): string | number | null {
    switch (key) {
        case 'time': return t.time
        case 'store': return t.storeName
        case 'receipt': return t.receiptNumber
        case 'channel': return transactionChannelName(t.channel)
        case 'units': return t.units
        case 'sales': return t.netSales
        case 'discounts': return t.discounts
        case 'bucks': return t.bucksCredited
        case 'return': return t.hasReturn ? 1 : 0
    }
}

/**
 * Sorts a copy of the receipts. Hand it the server's list, not an already sorted one: the sort is
 * stable, so receipts that tie keep the server's order — newest first — and never trade places from
 * one click to the next. A receipt with nothing in the column (no store, no receipt number) goes
 * after every one that has something, whichever way the column sorts.
 */
export function sortEmployeeTransactions(
    rows: readonly EmployeeTransaction[], key: TransactionColumnKey, dir: SortDir,
): EmployeeTransaction[] {
    const sign = dir === 'asc' ? 1 : -1
    return rows.slice().sort((a, b) => {
        const va = transactionSortValue(key, a)
        const vb = transactionSortValue(key, b)
        if (va === null || vb === null) return va === vb ? 0 : va === null ? 1 : -1
        if (typeof va === 'number' && typeof vb === 'number') return sign * (va < vb ? -1 : va > vb ? 1 : 0)
        const sa = String(va)
        const sb = String(vb)
        // The till's clock, yyyy-MM-ddTHH:mm:ss, sorts in time order as it stands.
        if (key === 'time') return sign * (sa < sb ? -1 : sa > sb ? 1 : 0)
        return sign * textCollator.compare(sa, sb)
    })
}

// ── A receipt's items ───────────────────────────────────────────────
//
// GET /api/dashboard/employee-performance/transactions/items takes the drill-down's own parameters
// plus a receipt's transactionId, and answers with every line on it — provided the receipt is one of
// that employee's in this very view; any other is a 404 that says no more than that. Under GROSS a
// returned line is listed but not counted, so the counted lines add up to the receipt's row. A
// line's cost never comes back, and nothing about the customer.

export interface EmployeeTransactionItem {
    lineId: string
    /** As the till kept it on the line; null when blank. */
    description: string | null
    /** The style number. */
    productNumber: string | null
    colorCode: string | null
    /** Null where the item comes in no colour ('Not Applicable'). */
    colorName: string | null
    size: string | null
    /** Below zero on a returned line. */
    quantity: number
    /** The price for one, before any discount. */
    unitPrice: number
    unitDiscount: number
    /** unitDiscount × quantity. */
    discount: number
    /** (unitPrice − unitDiscount) × quantity. */
    netSales: number
    /** Why staff gave the discount, where the reason's words are still on file. */
    discountReason: string | null
    /** A reason was recorded: the widget's staff-given discount. A discount without one is promotional. */
    staffGiven: boolean
    isReturn: boolean
    /**
     * In the receipt's figures for this view: every line under NET; under GROSS only the lines sold —
     * a quantity above zero — so neither a return nor a line rung at zero is.
     */
    counted: boolean
}

export interface EmployeeTransactionItemTotals {
    units: number
    netSales: number
    discounts: number
}

export interface EmployeeTransactionItemsData {
    transactionId: string
    /** Every line on the receipt, in the order the till saved them. */
    items: EmployeeTransactionItem[]
    /** Over the counted lines: the receipt's own row in the drill-down. */
    totals: EmployeeTransactionItemTotals
}

/** The drill-down's own query string — the widget's, plus the employee — and the receipt. */
export function buildEmployeeTransactionItemsUrl(
    period: EmployeePerformancePeriod, filters: EmployeePerformanceFilters, employeeId: string, transactionId: string,
): string {
    const p = employeePerformanceParams(period, filters)
    p.set('employeeId', employeeId)
    p.set('transactionId', transactionId)
    return `/api/dashboard/employee-performance/transactions/items?${p.toString()}`
}

export function fetchEmployeeTransactionItems(url: string, token: string | null): Promise<EmployeeTransactionItemsData> {
    return fetchEmployeeJson<EmployeeTransactionItemsData>(url, token)
}

/** The sentence an open receipt shows when its items can't be loaded; never echoes server internals. */
export function describeEmployeeTransactionItemsError(err: unknown): string {
    if (err instanceof EmployeePerformanceError) {
        if (err.status === 404) return err.message || 'This receipt isn’t in this view.'
        if (err.status === 403) return err.message || 'You don’t have access to this receipt.'
        if (err.status === 400) return err.message || 'This receipt’s items can’t be shown for these filters.'
    }
    // Reopening a receipt that failed asks again.
    return 'This receipt’s items could not be loaded. Close it and open it again.'
}

/** The line under an item's name: its style number, colour and size, as far as the till recorded them. */
export function transactionItemDetails(
    item: Pick<EmployeeTransactionItem, 'productNumber' | 'colorName' | 'colorCode' | 'size'>,
): string {
    return [
        item.productNumber && `Style ${item.productNumber}`,
        // The colour's name; its code where the name is gone.
        item.colorName ?? item.colorCode,
        item.size && `Size ${item.size}`,
    ].filter(Boolean).join(' · ')
}

/**
 * A return the receipt's figures leave out — under GROSS, which counts only what was sold. It is listed
 * all the same, dimmed and marked, and the closing line says returns were left out. GROSS doesn't count
 * a line rung at a quantity of zero either, but that line changes no figure whichever way it goes: it
 * is no return, nothing is missing for it, and it reads like any other line.
 */
export function isTransactionItemLeftOut(item: Pick<EmployeeTransactionItem, 'counted' | 'isReturn'>): boolean {
    return item.isReturn && !item.counted
}

/**
 * A line's discount, the widget's way: staff-given when a reason was recorded — with the reason's
 * words, where they are still on file — and promotional when none was (system pricing, multibuys).
 * Null for a line with no discount.
 */
export function transactionItemDiscountKind(
    item: Pick<EmployeeTransactionItem, 'discount' | 'staffGiven' | 'discountReason'>,
): string | null {
    if (item.discount === 0) return null
    if (!item.staffGiven) return 'Promotional'
    return item.discountReason ? `Staff-given · ${item.discountReason}` : 'Staff-given'
}
